import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, Upload, GraduationCap, UserPlus, FileText, Download, Eye, MoreVertical, Trash2, CheckCircle, XCircle, CreditCard, Edit, User, Phone, Mail, MapPin, Calendar, Briefcase, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import BulkImportDialog from '@/components/BulkImportDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeachersPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [pagination, setPagination] = useState(null);
  
  // Documents viewer state
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDocuments, setTeacherDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // View Details dialog state
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [teacherPhotoUrl, setTeacherPhotoUrl] = useState(null);
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusTeacher, setStatusTeacher] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  // Fetch teacher photo for view details
  const fetchTeacherPhoto = async (teacherId) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/teacher/${teacherId}`, { headers: getAuthHeader() });
      const docs = res.data.documents || [];
      // Get all photos and sort by uploaded_at to get the latest
      const photos = docs.filter(d => d.document_type === 'staff_photo')
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      const photoDoc = photos[0]; // Get the latest photo
      if (photoDoc?.cloudinary_url) {
        setTeacherPhotoUrl(photoDoc.cloudinary_url);
      } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
        setTeacherPhotoUrl(photoDoc.file_path);
      } else {
        setTeacherPhotoUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch photo:', error);
      setTeacherPhotoUrl(null);
    }
  };

  const openViewDetails = (teacher) => {
    setViewingTeacher(teacher);
    setTeacherPhotoUrl(null);
    setViewDetailsOpen(true);
    fetchTeacherPhoto(teacher.id);
  };

  useEffect(() => {
    fetchTeachers(1);
    fetchDocumentTypes();
  }, []);

  const fetchDocumentTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff-types`, { headers: getAuthHeader() });
      setDocumentTypes(res.data);
    } catch (error) {
      console.error('Failed to fetch document types');
    }
  };

  const fetchTeachers = async (page = 1) => {
    try {
      const response = await axios.get(`${API_URL}/api/teachers?page=${page}&page_size=${pageSize}`, {
        headers: getAuthHeader()
      });
      // Handle paginated response
      if (Array.isArray(response.data)) {
        setTeachers(response.data);
        setTotalTeachers(response.data.length);
        setPagination(null);
      } else {
        setTeachers(response.data.items || []);
        setTotalTeachers(response.data.total || 0);
        setPagination({
          page: response.data.page,
          total: response.data.total,
          total_pages: response.data.total_pages,
          has_next: response.data.has_next,
          has_prev: response.data.has_prev
        });
      }
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
      toast.error('Failed to load teachers');
    }
  };

  // Fetch teacher documents
  const fetchTeacherDocuments = async (teacher) => {
    setSelectedTeacher(teacher);
    setDocumentsDialogOpen(true);
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/teacher/${teacher.id}`, { headers: getAuthHeader() });
      setTeacherDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setTeacherDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Upload document
  // Upload document
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTeacher) return;
    
    // File size validation - Photos: 100 KB, Documents: 300 KB
    const isPhoto = docType === 'staff_photo';
    const maxSize = isPhoto ? 100 * 1024 : 300 * 1024; // 100 KB for photos, 300 KB for documents
    const maxSizeLabel = isPhoto ? '100 KB' : '300 KB';
    
    if (file.size > maxSize) {
      toast.error(`File size too large! Maximum allowed: ${maxSizeLabel}. Your file: ${(file.size / 1024).toFixed(1)} KB`);
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    
    try {
      await axios.post(`${API_URL}/api/documents/staff/upload/teacher/${selectedTeacher.id}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      fetchTeacherDocuments(selectedTeacher);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Download document
  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/staff/download/${docId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Delete document
  const deleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/staff/${docId}`, { headers: getAuthHeader() });
      toast.success('Document deleted');
      fetchTeacherDocuments(selectedTeacher);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Open status change dialog
  const openStatusDialog = (teacher, action) => {
    setStatusTeacher(teacher);
    if (action === 'deactivate') {
      setNewStatus('resigned');
    } else if (action === 'activate') {
      setNewStatus('active');
    }
    setStatusReason('');
    setStatusDialogOpen(true);
  };

  // Update teacher status
  const updateTeacherStatus = async () => {
    if (!statusTeacher || !newStatus) return;
    
    setStatusLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/teachers/${statusTeacher.id}/status?status=${newStatus}&reason=${encodeURIComponent(statusReason)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Teacher ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setStatusDialogOpen(false);
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Download ID Card
  const downloadIdCard = async (teacherId) => {
    try {
      const res = await axios.get(`${API_URL}/api/id-cards/teacher/${teacherId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `id_card_teacher_${teacherId.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('ID Card downloaded!');
    } catch (error) {
      toast.error('Failed to download ID Card');
    }
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (teacher.employee_id && teacher.employee_id.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DashboardLayout title="Teachers">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Teachers</CardTitle>
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalTeachers || teachers.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-teachers-input"
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="default" onClick={() => navigate('/teachers/new')} data-testid="new-teacher-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              New Teacher
            </Button>
            <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-teachers-btn">
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </Button>
          </div>
        </div>

        {/* Teachers List */}
        <Card data-testid="teachers-list-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>All Teachers</CardTitle>
              <CardDescription>
                Showing {filteredTeachers.length} of {totalTeachers || teachers.length} teachers
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API_URL}/api/teachers/export/excel`, {
                    headers: getAuthHeader(),
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `teachers_${new Date().toISOString().slice(0,10)}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Teachers exported successfully!');
                } catch (error) {
                  toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
                }
              }}
              data-testid="export-teachers-excel"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </CardHeader>
          <CardContent>
            {filteredTeachers.length === 0 ? (
              <div className="text-center py-8">
                <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No teachers found</p>
                <Button variant="outline" className="mt-4" onClick={() => setBulkImportOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Teachers from CSV
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium text-sm">Employee ID</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Phone</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Subjects</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeachers.map(teacher => (
                      <tr key={teacher.id} data-testid={`teacher-row-${teacher.id}`} className={`border-b hover:bg-muted/50 transition-colors ${teacher.status === 'inactive' || teacher.status === 'resigned' ? 'opacity-60 bg-muted/30' : ''}`}>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="font-mono">
                            {teacher.employee_id}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{teacher.name}</td>
                        <td className="py-3 px-4 text-sm">{teacher.email}</td>
                        <td className="py-3 px-4 text-sm">{teacher.phone}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {teacher.subject_specialization?.map((subject, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {subject}
                              </Badge>
                            )) || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={teacher.status === 'active' || !teacher.status ? 'default' : 'destructive'}>
                            {teacher.status || 'active'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`teacher-actions-${teacher.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openViewDetails(teacher)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/teachers/edit/${teacher.id}`)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Teacher
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => fetchTeacherDocuments(teacher)}>
                                <FileText className="h-4 w-4 mr-2" />
                                Documents & Photo
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => downloadIdCard(teacher.id)}>
                                <CreditCard className="h-4 w-4 mr-2" />
                                Download ID Card
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {(!teacher.status || teacher.status === 'active') ? (
                                <DropdownMenuItem 
                                  onClick={() => openStatusDialog(teacher, 'deactivate')}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <UserX className="h-4 w-4 mr-2" />
                                  Deactivate / Resign
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => openStatusDialog(teacher, 'activate')}
                                  className="text-green-600 focus:text-green-600"
                                >
                                  <UserCheck className="h-4 w-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {pagination.total_pages} ({pagination.total} total teachers)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchTeachers(currentPage - 1)}
                    disabled={!pagination.has_prev}
                    data-testid="teachers-prev-page"
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchTeachers(currentPage + 1)}
                    disabled={!pagination.has_next}
                    data-testid="teachers-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {newStatus === 'active' ? 'Reactivate Teacher' : 'Deactivate Teacher'}
              </DialogTitle>
              <DialogDescription>
                {newStatus === 'active' 
                  ? `Are you sure you want to reactivate ${statusTeacher?.name}? They will be able to login again.`
                  : `Are you sure you want to deactivate ${statusTeacher?.name}? Their login will be blocked and salary generation will stop.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {newStatus !== 'active' && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resigned">Resigned</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                      <SelectItem value="inactive">Inactive (Temporary)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea 
                  placeholder="Enter reason for status change..."
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={updateTeacherStatus} 
                disabled={statusLoading}
                variant={newStatus === 'active' ? 'default' : 'destructive'}
              >
                {statusLoading ? 'Updating...' : (newStatus === 'active' ? 'Reactivate' : 'Deactivate')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <BulkImportDialog
          open={bulkImportOpen}
          onOpenChange={setBulkImportOpen}
          type="teachers"
          onSuccess={fetchTeachers}
        />

        {/* Documents & Photo Upload Dialog */}
        <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents - {selectedTeacher?.name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDocs ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Loading documents...</p>
              </div>
            ) : (
              <Tabs defaultValue="uploaded" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="uploaded">
                    Uploaded ({teacherDocuments.length})
                  </TabsTrigger>
                  <TabsTrigger value="upload">
                    Upload New
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="uploaded" className="space-y-3 mt-4">
                  {teacherDocuments.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">No documents uploaded yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Upload photo first for ID Card</p>
                    </div>
                  ) : (
                    teacherDocuments.map(doc => (
                      <div 
                        key={doc.id} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${doc.document_type === 'staff_photo' ? 'bg-green-100' : 'bg-primary/10'}`}>
                            <FileText className={`h-5 w-5 ${doc.document_type === 'staff_photo' ? 'text-green-600' : 'text-primary'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {doc.document_type === 'staff_photo' ? '📷 Staff Photo' : doc.document_label}
                            </p>
                            <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadDocument(doc.id, doc.original_filename)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deleteDocument(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>
                
                <TabsContent value="upload" className="space-y-3 mt-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    📷 <strong>Upload Staff Photo first</strong> - It will appear on ID Card
                  </p>
                  <div className="grid md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {Object.entries(documentTypes).map(([key, info]) => {
                      const isUploaded = teacherDocuments.some(d => d.document_type === key);
                      const isPhoto = key === 'staff_photo';
                      return (
                        <div key={key} className={`p-2 border rounded-lg ${isUploaded ? 'bg-green-50 border-green-200' : isPhoto ? 'bg-blue-50 border-blue-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-medium">{isPhoto ? '📷 ' : ''}{info.label}</p>
                              {info.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                            </div>
                            {!isUploaded && (
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept={isPhoto ? '.jpg,.jpeg,.png' : '.jpg,.jpeg,.png,.pdf,.doc,.docx'}
                                  onChange={(e) => handleDocumentUpload(e, key)}
                                  disabled={uploading}
                                />
                                <div className="p-1 hover:bg-muted rounded">
                                  <Upload className="h-4 w-4 text-primary" />
                                </div>
                              </label>
                            )}
                            {isUploaded && <Badge variant="default" className="text-xs">✓</Badge>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* View Teacher Details Dialog */}
        <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Teacher Details - {viewingTeacher?.name}
              </DialogTitle>
            </DialogHeader>
            
            {viewingTeacher && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center overflow-hidden">
                    {teacherPhotoUrl ? (
                      <img src={teacherPhotoUrl} alt={viewingTeacher.name} className="w-full h-full object-cover" />
                    ) : (
                      <GraduationCap className="h-12 w-12 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{viewingTeacher.name}</h3>
                    <Badge variant="outline" className="font-mono">{viewingTeacher.employee_id}</Badge>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" /> {viewingTeacher.designation || '-'}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Joined: {viewingTeacher.joining_date || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{viewingTeacher.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{viewingTeacher.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{viewingTeacher.present_address || viewingTeacher.address || '-'}</span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm border-b pb-1">Personal Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Gender:</span><span>{viewingTeacher.gender || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Date of Birth:</span><span>{viewingTeacher.date_of_birth || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Blood Group:</span><span>{viewingTeacher.blood_group || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Aadhaar:</span><span>{viewingTeacher.aadhaar_number || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">PAN:</span><span>{viewingTeacher.pan_number || '-'}</span></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-sm border-b pb-1">Professional Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Designation:</span><span>{viewingTeacher.designation || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Department:</span><span>{viewingTeacher.department || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Qualification:</span><span>{viewingTeacher.highest_qualification || viewingTeacher.qualification || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Experience:</span><span>{viewingTeacher.total_experience_years || viewingTeacher.experience || '-'} years</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Employment:</span><span>{viewingTeacher.employment_type || '-'}</span></div>
                    </div>
                  </div>
                </div>

                {/* Subjects */}
                {viewingTeacher.subject_specialization?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Subjects</h4>
                    <div className="flex flex-wrap gap-2">
                      {viewingTeacher.subject_specialization.map((sub, i) => (
                        <Badge key={i} variant="secondary">{sub}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bank Details (if available) */}
                {(viewingTeacher.bank_name || viewingTeacher.account_number) && (
                  <div className="space-y-3 p-4 border rounded-lg">
                    <h4 className="font-medium text-sm">Bank Details</h4>
                    <div className="grid md:grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Bank:</span><span>{viewingTeacher.bank_name || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Account:</span><span>{viewingTeacher.account_number || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">IFSC:</span><span>{viewingTeacher.ifsc_code || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Salary:</span><span>₹{viewingTeacher.basic_salary || viewingTeacher.salary || '-'}</span></div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" className="flex-1" onClick={() => { setViewDetailsOpen(false); navigate(`/teachers/edit/${viewingTeacher.id}`); }}>
                    <Edit className="h-4 w-4 mr-2" /> Edit Teacher
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { setViewDetailsOpen(false); fetchTeacherDocuments(viewingTeacher); }}>
                    <FileText className="h-4 w-4 mr-2" /> Documents
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => downloadIdCard(viewingTeacher.id)}>
                    <CreditCard className="h-4 w-4 mr-2" /> ID Card
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeachersPage;
